<template>
  <UPage class="flex-1 flex flex-col min-h-0" :ui="{ center: 'flex flex-col flex-1 min-h-0' }">
    <div
      class="sticky top-0 z-10 flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-200 dark:border-gray-800 bg-white/75 dark:bg-gray-900/75 backdrop-blur px-4"
    >
      <UNavigationMenu
        :items="navItems"
        variant="link"
        highlight
        class="-mb-px shrink-0 w-full sm:w-auto overflow-x-auto"
        :ui="{ list: 'flex-nowrap', item: 'shrink-0' }"
      />

      <div class="flex items-center gap-2 py-2 shrink-0">
        <UButton
          v-if="activeTab?.slot === 'invoice'"
          :loading="invoiceRef?.syncing"
          color="neutral"
          icon="i-lucide-refresh-cw"
          label="RAG Sync"
          variant="outline"
          @click="invoiceRef?.syncInvoice()"
        />
        <UButton
          :loading="saving"
          :disabled="!canWrite"
          color="primary"
          icon="i-lucide-check"
          label="Speichern"
          @click="saveCurrentTab"
        />
      </div>
    </div>

    <UPageBody class="flex-1 flex flex-col min-h-0 p-4 sm:p-6 lg:p-8 !pt-2 sm:!pt-4 lg:!pt-6">
      <div v-if="activeTab?.slot === 'general'" class="space-y-6">
        <UCard>
          <div class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <UFormField label="Mandantename" required>
                <UInput
                  v-model="tenantState.name"
                  :disabled="!canWrite"
                  class="w-full"
                  placeholder="Mandant 1"
                  @update:model-value="markDirty"
                />
              </UFormField>

              <UFormField label="Primärfarbe" required>
                <USelect
                  v-model="tenantState.primaryColor"
                  :disabled="!canWrite"
                  :items="[
                    'blue',
                    'green',
                    'red',
                    'orange',
                    'yellow',
                    'purple',
                    'pink',
                    'indigo',
                    'cyan',
                    'teal',
                    'gray',
                  ]"
                  class="w-full"
                  @update:model-value="markDirty"
                />
              </UFormField>

              <UFormField label="Standardmandant">
                <UCheckbox
                  v-model="tenantState.isDefault"
                  :disabled="!canWrite"
                  label="Diesen Mandanten als Standard festlegen"
                  @update:model-value="markDirty"
                />
              </UFormField>
            </div>

            <div class="pt-4 border-t border-gray-200 dark:border-gray-800">
              <UFormField label="JWT Token">
                <div class="flex flex-col sm:flex-row gap-2">
                  <UInput
                    :type="showJwt ? 'text' : 'password'"
                    :value="currentJwt"
                    class="flex-1 font-mono text-xs overflow-hidden"
                    readonly
                  />
                  <div class="flex gap-2 justify-end">
                    <UButton
                      :icon="showJwt ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                      color="neutral"
                      variant="ghost"
                      @click="showJwt = !showJwt"
                    />
                    <UButton
                      color="primary"
                      icon="i-lucide-copy"
                      label="Kopieren"
                      class="flex-1 sm:flex-none justify-center"
                      @click="copyJwtToClipboard"
                    />
                  </div>
                </div>
                <template #description>
                  Dieser Token ist ein Jahr gültig und ausschließlich für den Invoice Compare Service bestimmt.
                </template>
              </UFormField>
            </div>
          </div>
        </UCard>
      </div>

      <div v-else-if="activeTab?.slot === 'permissions'" class="space-y-6">
        <UCard>
          <div class="space-y-6">
            <UFormField
              label="Erlaubte E-Mail Regex"
              description="Eine Regex pro Zeile. Benutzer mit passender E-Mail dürfen diesen Mandanten betreten."
            >
              <UTextarea
                v-model="tenantState.allowedEmailRegexText"
                :disabled="!canWrite"
                class="w-full font-mono text-sm"
                :rows="5"
                placeholder="(?i)^.*@customer-a\.com$"
                @update:model-value="markDirty"
              />
            </UFormField>

            <div class="pt-4 border-t border-gray-200 dark:border-gray-800">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-lg font-medium">Gruppen-Rollen-Mapping</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {{
                      localMode
                        ? 'Weisen Sie lokalen Gruppen Rollen in diesem Mandanten zu.'
                        : 'Weisen Sie OIDC- / Entra-Gruppen Rollen in diesem Mandanten zu.'
                    }}
                  </p>
                </div>
                <UButton
                  v-if="canWrite"
                  color="neutral"
                  icon="i-lucide-plus"
                  label="Zuordnung hinzufügen"
                  variant="outline"
                  size="sm"
                  @click="addGroupMapping"
                />
              </div>

              <!-- Quick pick from recognized user groups if available -->
              <div
                v-if="!localMode && availableUnmappedUserGroups.length > 0 && canWrite"
                class="p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg space-y-2 mb-4"
              >
                <div class="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Im Login erkannte Gruppen schnell hinzufügen:
                </div>
                <div class="flex flex-wrap gap-2">
                  <UButton
                    v-for="group in availableUnmappedUserGroups"
                    :key="group.id"
                    color="neutral"
                    icon="i-lucide-plus"
                    :label="`${group.displayName} (${group.id})`"
                    size="xs"
                    variant="outline"
                    @click="addKnownGroupMapping(group)"
                  />
                </div>
              </div>

              <!-- Table header -->
              <div
                v-if="tenantState.groupMappings.length > 0"
                class="hidden sm:grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 uppercase px-3 pb-1"
              >
                <div :class="localMode ? 'col-span-9' : 'col-span-5'">
                  {{ localMode ? 'Lokale Gruppe' : 'Gruppen-ID (Object ID / Name)' }}
                </div>
                <div v-if="!localMode" class="col-span-4">Anzeigename</div>
                <div class="col-span-2">Rollen</div>
                <div class="col-span-1"></div>
              </div>

              <div class="space-y-3">
                <div
                  v-for="(mapping, index) in tenantState.groupMappings"
                  :key="index"
                  class="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700"
                >
                  <div v-if="localMode" class="sm:col-span-9">
                    <LocalGroupPicker
                      v-if="localGroupsAvailable"
                      :model-value="mapping.groupId"
                      :groups="localIdentity.groups.value"
                      :disabled="!canWrite"
                      @update:model-value="(key: string) => onLocalGroupSelect(mapping, key)"
                    />
                    <span v-else class="text-sm" data-test="mapping-group-name">
                      {{ mapping.displayName }}
                    </span>
                  </div>
                  <div v-if="!localMode" class="sm:col-span-5">
                    <UInput
                      v-model="mapping.groupId"
                      :disabled="!canWrite"
                      class="w-full font-mono text-xs"
                      placeholder="Gruppen-ID (z. B. GUID oder Name)"
                      data-test="mapping-group-id-input"
                      @update:model-value="() => onGroupIdChange(mapping)"
                    />
                  </div>
                  <div v-if="!localMode" class="sm:col-span-4">
                    <UInput
                      v-model="mapping.displayName"
                      :disabled="!canWrite"
                      class="w-full text-sm"
                      placeholder="Anzeigename (z. B. Buchhaltung)"
                      @update:model-value="markDirty"
                    />
                  </div>
                  <div class="sm:col-span-2">
                    <USelect
                      v-model="mapping.roles"
                      multiple
                      :disabled="!canWrite"
                      :items="withAssignedRoles(availableRoles, mapping.roles)"
                      class="w-full text-sm"
                      placeholder="Rollen"
                      @update:model-value="markDirty"
                    />
                  </div>
                  <div class="sm:col-span-1 flex justify-end">
                    <UButton
                      v-if="canWrite"
                      color="danger"
                      icon="i-lucide-trash"
                      variant="ghost"
                      size="sm"
                      data-test="mapping-remove-btn"
                      @click="removeGroupMapping(index)"
                    />
                  </div>
                </div>
                <div
                  v-if="tenantState.groupMappings.length === 0"
                  class="text-sm text-gray-500 italic py-2"
                >
                  Keine Gruppen-Rollen-Zuordnungen definiert.
                </div>
              </div>
            </div>
          </div>
        </UCard>
      </div>

      <div v-else-if="activeTab?.slot === 'statistics' && activeTenant">
        <StatisticsPage
          :customer-no="activeTenant.customerNo"
          :tenant-name="activeTenant.tenantName"
          is-component
        />
      </div>

      <div v-else-if="activeTab?.slot === 'microtech' && activeTenant">
        <MicrotechConfig
          ref="microtechRef"
          :customer-no="activeTenant.customerNo"
          :tenant-name="activeTenant.tenantName"
          is-component
          :can-write="canWrite"
          @dirty="markDirty"
        />
      </div>

      <div v-else-if="activeTab?.slot === 'elo' && activeTenant">
        <EloConfig
          ref="eloRef"
          :customer-no="activeTenant.customerNo"
          :tenant-name="activeTenant.tenantName"
          is-component
          :can-write="canWrite"
          @dirty="markDirty"
        />
      </div>

      <div v-else-if="activeTab?.slot === 'mail' && activeTenant">
        <MailConfig
          ref="mailRef"
          :customer-no="activeTenant.customerNo"
          :tenant-name="activeTenant.tenantName"
          is-component
          :can-write="canWrite"
          @dirty="markDirty"
        />
      </div>

      <div v-else-if="activeTab?.slot === 'invoice' && activeTenant">
        <InvoiceConfig
          ref="invoiceRef"
          :customer-no="activeTenant.customerNo"
          :tenant-name="activeTenant.tenantName"
          is-component
          :can-write="canWrite"
          @dirty="markDirty"
        />
      </div>

      <div v-else-if="activeTab?.slot === 'workflow' && activeTenant">
        <WorkflowConfig
          ref="workflowRef"
          :customer-no="activeTenant.customerNo"
          :tenant-name="activeTenant.tenantName"
          is-component
          :can-write="canWrite"
          @dirty="markDirty"
        />
      </div>

      <div v-else-if="activeTab?.slot === 'nfon' && activeTenant">
        <NfonConfig
          ref="nfonRef"
          :customer-no="activeTenant.customerNo"
          :tenant-name="activeTenant.tenantName"
          is-component
          :can-write="canWrite"
          @dirty="markDirty"
        />
      </div>
    </UPageBody>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import axios from 'axios'
import { PERMISSIONS, hasPermission, rolesOf, withAssignedRoles, type GroupRoleMapping } from '@/utils/roles'
import { backendErrorMessage } from '@/utils/api-errors'
import StatisticsPage from './StatisticsPage.vue'
import MicrotechConfig from './MicrotechConfig.vue'
import EloConfig from './EloConfig.vue'
import MailConfig from './MailConfig.vue'
import InvoiceConfig from './InvoiceConfig.vue'
import WorkflowConfig from './WorkflowConfig.vue'
import NfonConfig from './NfonConfig.vue'
import LocalGroupPicker from '@/components/LocalGroupPicker.vue'
import { useLocalIdentity } from '@/composables/useLocalIdentity'

const authStore = useAuthStore()
const { userInfo } = storeToRefs(authStore)
const activeTenant = computed(() => userInfo.value?.activeTenant)
const canWrite = computed(() => hasPermission(userInfo.value?.user.permissions, PERMISSIONS.SETTINGS_WRITE))
const toast = useToast()
const route = useRoute()

const activeTabIndex = ref(0)
const saving = ref(false)
const hasChanges = ref(false)
const showJwt = ref(false)
const currentJwt = ref('')

const serviceStatuses = ref({
  microtech: false,
  elo: false,
  mail: false,
  invoice: false,
  workflow: false,
  nfon: false,
})

const appConfig = useAppConfig()
const tenantState = reactive({
  name: '',
  primaryColor: 'blue',
  isDefault: false,
  allowedEmailRegexText: '',
  groupMappings: [] as { groupId: string; displayName: string; roles: string[] }[],
})

const availableRoles = [
  { label: 'Tenant Viewer', value: 'TENANT_VIEWER' },
  { label: 'Tenant Admin Viewer', value: 'TENANT_ADMIN_VIEWER' },
  { label: 'Tenant Admin', value: 'TENANT_ADMIN' },
  { label: 'Tenant Data Viewer', value: 'TENANT_DATA_VIEWER' },
  { label: 'Tenant VAT Viewer', value: 'TENANT_VAT_VIEWER' },
]

const availableUnmappedUserGroups = computed(() => {
  const details = userInfo.value?.user?.groupDetails || []
  const mapped = new Set(tenantState.groupMappings.map((m) => m.groupId.toLowerCase().trim()))
  return details.filter((g) => g.id && !mapped.has(g.id.toLowerCase().trim()))
})

const addKnownGroupMapping = (group: { id: string; displayName: string }) => {
  tenantState.groupMappings.push({
    groupId: group.id,
    displayName: group.displayName,
    roles: ['TENANT_VIEWER'],
  })
  markDirty()
}

// Lokale Anmeldung: Gruppen kommen aus der Registry statt als freier Text. Die
// Einstellungen lassen sich nur mit Kunden-Leserecht laden; fehlen sie, bleibt
// die Zuordnung sichtbar (Namen aus dem Mapping), lässt sich aber nicht neu wählen.
const customerNo = computed(() => activeTenant.value?.customerNo ?? '')
const localIdentity = useLocalIdentity(customerNo)
const tenantLoginMode = ref('')
const localMode = computed(
  () => localIdentity.settings.value?.loginMode === 'local' || tenantLoginMode.value === 'local',
)
const localGroupsAvailable = computed(() => localIdentity.groups.value.length > 0)

const onLocalGroupSelect = (mapping: { groupId: string; displayName: string }, key: string) => {
  mapping.groupId = key
  mapping.displayName =
    localIdentity.groups.value.find((g) => `local:${g.id}` === key)?.name ?? key
  markDirty()
}

const addGroupMapping = () => {
  tenantState.groupMappings.push({ groupId: '', displayName: '', roles: ['TENANT_VIEWER'] })
  markDirty()
}

const removeGroupMapping = (index: number) => {
  tenantState.groupMappings.splice(index, 1)
  markDirty()
}

const onGroupIdChange = (mapping: { groupId: string; displayName: string; roles: string[] }) => {
  markDirty()
  if (!mapping.displayName || mapping.displayName === mapping.groupId) {
    const known = userInfo.value?.user?.groupDetails?.find(
      (g) => g.id.toLowerCase().trim() === mapping.groupId.toLowerCase().trim(),
    )
    if (known) {
      mapping.displayName = known.displayName
    }
  }
}

watch(
  () => tenantState.primaryColor,
  (color) => {
    if (color) {
      appConfig.ui.colors.primary = color
    }
  },
)

const microtechRef = ref()
const eloRef = ref()
const mailRef = ref()
const invoiceRef = ref()
const workflowRef = ref()
const nfonRef = ref()

const tabs = [
  { label: 'Allgemein', slot: 'general', icon: 'i-lucide-settings' },
  { label: 'Berechtigungen', slot: 'permissions', icon: 'i-lucide-shield-check' },
  { label: 'Statistik', slot: 'statistics', icon: 'i-lucide-chart-bar' },
  { label: 'microtech', slot: 'microtech', icon: 'i-lucide-database' },
  { label: 'ELO', slot: 'elo', icon: 'i-lucide-archive' },
  { label: 'Mail', slot: 'mail', icon: 'i-lucide-mail' },
  { label: 'Invoice', slot: 'invoice', icon: 'i-lucide-file-text' },
  { label: 'Workflow', slot: 'workflow', icon: 'i-lucide-git-branch' },
  { label: 'NFON', slot: 'nfon', icon: 'i-lucide-phone' },
]

const navItems = computed(() =>
  tabs.map((tab) => {
    const status = serviceStatuses.value[tab.slot as keyof typeof serviceStatuses.value]
    const hasStatus = Object.keys(serviceStatuses.value).includes(tab.slot)

    return {
      label: tab.label,
      icon: tab.icon,
      to: `/admin/${tab.slot}`,
      chip: hasStatus
        ? {
            color: status ? 'success' : 'error',
            size: 'sm',
          }
        : undefined,
    }
  }),
)

const activeTab = computed(() => tabs[activeTabIndex.value])

const markDirty = () => {
  hasChanges.value = true
}

const fetchJwt = async () => {
  if (!activeTenant.value) return
  try {
    const response = await axios.get('/api/registry/v1/jwt')
    currentJwt.value = response.data.jwt
  } catch (error) {
    console.error('Failed to fetch JWT', error)
  }
}

const fetchServiceStatuses = async () => {
  if (!activeTenant.value) return

  try {
    const [microtech, elo, mail, invoice, workflow, nfon] = await Promise.allSettled([
      axios.get(`/api/microtech/v1/config`),
      axios.get(`/api/registry/v1/elo/config`),
      axios.get(`/api/mail/v1/config`),
      axios.get(`/api/invoice/v1/config`),
      axios.get(`/api/workflow/v1/config`),
      axios.get(`/api/nfon/v1/config`),
    ])

    if (microtech.status === 'fulfilled') serviceStatuses.value.microtech = microtech.value.data.active
    if (elo.status === 'fulfilled') serviceStatuses.value.elo = elo.value.data.active
    if (mail.status === 'fulfilled') serviceStatuses.value.mail = mail.value.data.active
    if (invoice.status === 'fulfilled') serviceStatuses.value.invoice = invoice.value.data.active
    if (workflow.status === 'fulfilled') serviceStatuses.value.workflow = workflow.value.data.active
    if (nfon.status === 'fulfilled') serviceStatuses.value.nfon = nfon.value.data.active
  } catch (error) {
    console.error('Failed to fetch service statuses', error)
  }
}

const copyJwtToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(currentJwt.value)
    toast.add({
      title: 'Erfolg',
      description: 'JWT Token in die Zwischenablage kopiert.',
      color: 'success',
    })
  } catch (error) {
    console.error('Failed to copy JWT', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Kopieren des JWT Tokens.',
      color: 'danger',
    })
  }
}

const saveCurrentTab = async () => {
  const currentTab = tabs[activeTabIndex.value].slot
  saving.value = true
  try {
    if (currentTab === 'general' || currentTab === 'permissions') {
      await saveGeneral()
    } else if (currentTab === 'microtech' && microtechRef.value?.saveConfig) {
      await microtechRef.value.saveConfig()
    } else if (currentTab === 'elo' && eloRef.value?.saveConfig) {
      await eloRef.value.saveConfig()
    } else if (currentTab === 'mail' && mailRef.value?.saveConfig) {
      await mailRef.value.saveConfig()
    } else if (currentTab === 'invoice' && invoiceRef.value?.saveConfig) {
      await invoiceRef.value.saveConfig()
    } else if (currentTab === 'workflow' && workflowRef.value?.saveConfig) {
      await workflowRef.value.saveConfig()
    } else if (currentTab === 'nfon' && nfonRef.value?.saveConfig) {
      await nfonRef.value.saveConfig()
    }
    await fetchServiceStatuses()
    hasChanges.value = false
  } catch (error) {
    console.error(`Failed to save ${currentTab}`, error)
  } finally {
    saving.value = false
  }
}

const saveGeneral = async () => {
  if (!activeTenant.value) return

  // Convert mappings back to groupRoleMapping and groupNameMapping
  const groupRoleMapping: Record<string, string[]> = {}
  const groupNameMapping: Record<string, string> = {}
  tenantState.groupMappings.forEach((m) => {
    const gId = m.groupId?.trim()
    const roles = rolesOf(m.roles)
    if (gId && roles.length > 0) {
      // Several rows may name the same group; their roles are merged instead of
      // the last row silently replacing the earlier ones.
      groupRoleMapping[gId] = [...new Set([...(groupRoleMapping[gId] || []), ...roles])]
      const name = m.displayName?.trim()
      // The display name falls back to the group ID in the UI; persisting it
      // would fill groupNameMapping with meaningless id -> id entries.
      if (name && name !== gId) {
        groupNameMapping[gId] = name
      }
    }
  })

  try {
    await axios.put(
      `/api/registry/v1/customers/${activeTenant.value.customerNo}/tenants/${activeTenant.value.tenantName}`,
      {
        name: tenantState.name,
        primaryColor: tenantState.primaryColor,
        isDefault: tenantState.isDefault,
        customer: { customerNo: activeTenant.value.customerNo }, // Minimal customer info
        allowedEmailRegex: tenantState.allowedEmailRegexText
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        groupRoleMapping,
        groupNameMapping,
      },
    )
    toast.add({ title: 'Erfolg', description: 'Mandant aktualisiert.', color: 'success' })
    // Reload user info to refresh the sidebar/app color if needed
    await authStore.fetchUserInfo()
    // Reload tenant details to refresh local state
    await fetchTenantDetails()
  } catch (error) {
    toast.add({
      title: 'Fehler',
      description: backendErrorMessage(error, 'Fehler beim Speichern.'),
      color: 'danger',
    })
    throw error
  }
}

const fetchTenantDetails = async () => {
  if (!activeTenant.value) return
  try {
    const response = await axios.get(
      `/api/registry/v1/customers/${activeTenant.value.customerNo}/tenants/${activeTenant.value.tenantName}`,
    )
    const t = response.data.tenant
    tenantState.name = t.name
    tenantState.primaryColor = t.primaryColor || 'blue'
    tenantState.isDefault = !!t.isDefault
    tenantState.allowedEmailRegexText = (t.allowedEmailRegex || []).join('\n')
    tenantLoginMode.value = t.customer?.loginMode ?? ''
    const nameMap = t.groupNameMapping || {}
    const roleMap: GroupRoleMapping = t.groupRoleMapping || {}
    tenantState.groupMappings = Object.entries(roleMap).map(([groupId, roles]) => ({
      groupId,
      displayName: nameMap[groupId] || groupId,
      roles: rolesOf(roles),
    }))
  } catch (error) {
    console.error('Failed to fetch tenant details', error)
  }
}

const updateTabFromRoute = () => {
  const tabSlot = route.params.tab as string
  hasChanges.value = false
  if (tabSlot) {
    const index = tabs.findIndex((t) => t.slot === tabSlot)
    if (index !== -1) {
      activeTabIndex.value = index
    }
  } else {
    activeTabIndex.value = 0
  }
}

onMounted(async () => {
  updateTabFromRoute()
  if (activeTenant.value) {
    await Promise.all([fetchTenantDetails(), localIdentity.load()])
    fetchJwt()
    fetchServiceStatuses()
  }
})

onUnmounted(() => {
  // Revert color if not saved
  if (activeTenant.value?.primaryColor) {
    appConfig.ui.colors.primary = activeTenant.value.primaryColor
  }
})

watch(
  () => route.params.tab,
  () => {
    updateTabFromRoute()
  },
)
</script>
